import * as htmlparser from 'htmlparser2';
import Typo from 'typo-js';

import { isAbbreviation } from './abbreviationsWhitelist';

const dictionary = new Typo('en_US', false, false, {
  dictionaryPath: `${process.env.PUBLIC_URL}/dictionaries`,
});

const scTag = 'span';
const scClass = 'misspelling';

const wordPattern = /[a-zA-Z]+(?:['’][a-zA-Z]+)*/g;

const normalizeApostrophes = (value) => value.replaceAll('’', "'");

const removeOuterPunctuation = (value) =>
  value.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');

const markSpellCheckedWords = (text) =>
  // we look for whitespace and non-whitespace chunks of text
  text.replace(/(\s+|[^\s]+)/g, (chunk) => {
    // if white space, keep it as-is
    if (/\s/.test(chunk)) {
      return chunk;
    }

    const normalizedChunk = normalizeApostrophes(removeOuterPunctuation(chunk));

    // Skip whitelisted military abbreviations (TYCOM, NAVSEA, 3-M, ...) so they aren't flagged
    // The list comes from the audio_abbreviation table
    if (normalizedChunk && isAbbreviation(normalizedChunk)) {
      return chunk;
    }

    // Check each word separately while preserving contractions and punctuation
    return chunk.replace(wordPattern, (originalWord) => {
      const normalizedWord = normalizeApostrophes(originalWord);

      // Check simple words against the dynamic abbreviations whitelist
      if (isAbbreviation(normalizedWord)) {
        return originalWord;
      }

      if (!dictionary.check(normalizedWord)) {
        return `<${scTag} class="${scClass}">${originalWord}</${scTag}>`;
      }

      return originalWord;
    });
  });

const processHtmlContent = (html) => {
  // Ensure html is valid
  html = typeof html === 'string' ? html : '<p></p>';

  let resultHtml = '';
  let currentText = '';
  let formulaDepth = 0;
  const tagStack = [];

  const flushCurrentText = () => {
    if (!currentText) return;

    if (formulaDepth > 0) {
      // Keep formula contents untouched so KaTeX renders correctly
      resultHtml += currentText;
    } else {
      // we spell check the accumulated text
      resultHtml += markSpellCheckedWords(currentText);
    }

    currentText = '';
  };

  const parser = new htmlparser.Parser({
    onopentag(name, attrs) {
      flushCurrentText();

      const isFormula = (attrs.class || '').split(/\s+/).includes('ql-formula');

      // we keep the opening tag and it's attributes if any
      resultHtml += `<${name}${Object.keys(attrs)
        .map((key) => ` ${key}="${attrs[key]}"`)
        .join('')}>`;

      // we keep track of nested tags
      tagStack.push(name);

      // Ignore all text inside formula nodes
      if (isFormula || formulaDepth > 0) {
        formulaDepth++;
      }
    },

    ontext(text) {
      // we add it to the text to be spell checked
      currentText += text;
    },

    onclosetag(name) {
      flushCurrentText();

      // if it is the closing tag, we remove the tag from the stack
      if (tagStack.length > 0 && tagStack[tagStack.length - 1] === name) {
        tagStack.pop();

        if (formulaDepth > 0) {
          formulaDepth--;
        }

        resultHtml += `</${name}>`;
      }
    },

    onend() {
      flushCurrentText();
    },
  });

  parser.write(html);
  parser.end();

  return resultHtml;
};

export const spellCheckContent = (html) => processHtmlContent(html);
